import {
  Box,
  Button,
  Divider,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import SecondaryBox from "../../../atoms/box/SecondaryBox";
import { NavbarIcon } from "../../../atoms/navbar/Icon";
import CKEditorField from "../../../components/custom-fields/CKEditorField";
import { SmallModal } from "../../../components/SmallModal";
import { SwitchWithTitle } from "../../../components/SwitchWithTitle";
import { openSnackbar } from "../../../context/SnackbarContext";
import {
  DeploymentPermission,
  DeploymentStatus,
  HeaderVariable,
  Runnable,
} from "../../../models/types";
import { TableTextWithEllipsis } from "../../../molecules/TableTextWithEllipsis";
import { createDeployment } from "../../../services/DeploymentService";
import { openIntercom } from "../../../services/IntercomService";
import {
  DeploymentPermissionLabels,
  DeploymentPermissionValues,
} from "../../../utilities/DeploymentHelper";
import { CustomAxiosError } from "../../../utilities/ErrorResponseHelper";
import { CustomInputValidation } from "../../CustomInputValidation";
import { Headers } from "../../Headers";

type CreateDeploymentModalProps = {
  /** Should the modal be open? */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  runnable: Runnable;
  onCreate: (val: string) => void;
};

export const CreateDeploymentModal = (props: CreateDeploymentModalProps) => {
  const { open, onClose, runnable, onCreate } = props;
  const [name, setName] = useState<string>("");
  const [guardrail, setGuardrail] = useState<any>({});
  const [description, setDescription] = useState<string>("");
  const [readPermission, setReadPermission] = useState<DeploymentPermission>(
    DeploymentPermission.EVERYONE
  );
  const [editPermission, setEditPermission] = useState<DeploymentPermission>(
    DeploymentPermission.EVERYONE
  );
  const [readRoles, setReadRoles] = useState<string[] | null>([]);
  const [editRoles, setEditRoles] = useState<string[] | null>([]);
  const [variables, setVariables] = useState<string[] | null>(null);
  const [secrets, setSecrets] = useState<string[] | null>(null);
  const theme = useTheme();

  const clean = () => {
    setVariables(null);
    setSecrets(null);
    setDescription("");
    setName("");
    setReadPermission(DeploymentPermission.EVERYONE);
    setEditPermission(DeploymentPermission.EVERYONE);
    setReadRoles([]);
    setEditRoles([]);
    setGuardrail({});
  };

  const handleCreateDeployment = async () => {
    try {
      const parsedVariables = [...(variables ? variables : []), ...(secrets ? secrets : [])].map(
        (
          obj: string
        ): {
          secret: boolean;
          key: string;
          value: string;
        } => {
          const parsed: HeaderVariable = JSON.parse(obj);
          return {
            secret: parsed.type === "Secret",
            key: parsed.key,
            value: parsed.value.id,
          };
        }
      );

      const created = await createDeployment({
        name,
        description,
        runnable: runnable.id,
        variables_mapping: parsedVariables,
        read_permission: readPermission,
        edit_permission: editPermission,
        edit_authorized_roles: editRoles ?? [],
        read_authorized_roles: readRoles ?? [],
        guardrails_settings: guardrail,
      });

      openSnackbar("Runnable successfully deployed", "success");
      clean();
      onClose();
      onCreate(created.id);
    } catch (e) {
      if (e instanceof CustomAxiosError) {
        e.showAuditLogSnackbar("Failed to create deployment");
      } else {
        openSnackbar("Failed to create deployment", "error");
      }
    }
  };

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      size="medium"
      position="right"
      isLoading={false}
      title="Create New Deployment"
      action={
        <Button onClick={() => handleCreateDeployment()} fullWidth>
          Deploy Runnable
        </Button>
      }
    >
      <Box display="flex" flexDirection="column" gap="20px" width="100%">
        <RunnableDetailsSection runnable={runnable} />
        <DeploymentDetailsSection
          readRoles={readRoles}
          setReadRoles={setReadRoles}
          editRoles={editRoles}
          setEditRoles={setEditRoles}
          editPermission={editPermission}
          setEditPermission={setEditPermission}
          readPermission={readPermission}
          setReadPermission={setReadPermission}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
        />
        <Headers
          border
          expandable
          title="Variables"
          onChange={setVariables}
          value={variables}
          addTitle="Add Variable"
          type="Variable"
          hideEmptyAction
          emptyLabel="No Variables"
          emptyDescription="No variable was added to this deployment yet"
        />
        <Headers
          border
          expandable
          title="Secrets"
          onChange={setSecrets}
          value={secrets}
          addTitle="Add Secret"
          type="Secret"
          hideEmptyAction
          emptyLabel="No Secrets"
          emptyDescription="No secret was added to this deployment yet"
        />
        <GuardrailDetailsSection guardrail={guardrail} setGuardRail={setGuardrail} />
      </Box>
    </SmallModal>
  );
};

type RunnableDetailsSectionProps = {
  runnable: Runnable;
};

const RunnableDetailsSection = ({ runnable }: RunnableDetailsSectionProps) => {
  return (
    <Box display="flex" flexDirection="row" gap="20px" alignItems="center">
      <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
        <Typography variant="h3">Runnable ID</Typography>
        <TableTextWithEllipsis fullWidth={false} value={runnable.id} />
      </Box>
      <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
        <Typography variant="h3">Runnable</Typography>
        <TableTextWithEllipsis value={`${runnable.name}-${runnable.version}`} />
      </Box>
    </Box>
  );
};

type DeploymentDetailsSectionProps = {
  name: string;
  setName: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  readPermission: DeploymentPermission;
  setReadPermission: (val: DeploymentPermission) => void;
  editPermission: DeploymentPermission;
  setEditPermission: (val: DeploymentPermission) => void;
  readRoles: string[] | null;
  setReadRoles: (val: string[] | null) => void;
  editRoles: string[] | null;
  setEditRoles: (val: string[] | null) => void;
  status?: string;
  setStatus?: (value: DeploymentStatus) => void;
};

export const DeploymentDetailsSection = ({
  name,
  setName,
  description,
  setDescription,
  readPermission,
  setReadPermission,
  editPermission,
  setEditPermission,
  readRoles,
  setReadRoles,
  editRoles,
  setEditRoles,
  status,
  setStatus,
}: DeploymentDetailsSectionProps) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const theme = useTheme();
  return (
    <SecondaryBox>
      <Box display="flex" flexDirection="row" alignItems={"center"} gap="10px">
        <Box
          sx={{ cursor: "pointer" }}
          onClick={() => setExpanded(!expanded)}
          alignItems="center"
          display="flex"
        >
          <NavbarIcon variant={expanded ? "chevron-up-sm" : "chevron-down-sm"} />
        </Box>
        <Typography sx={{ cursor: "pointer" }} onClick={() => setExpanded(!expanded)} variant="h3">
          Deployment Details
        </Typography>
      </Box>
      {expanded && (
        <>
          <Divider sx={{ paddingTop: "10px" }} />

          <Box display="flex" paddingTop="10px" flexDirection="column" gap="10px">
            <Typography variant="h3">Name</Typography>
            <TextField
              error={false}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              required
              label="Summary"
            />
            <Typography variant="h3">Description</Typography>
            <CKEditorField value={description} onChange={setDescription} />
            {status && setStatus && (
              <>
                <Typography variant="h3">Status</Typography>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DeploymentStatus)}
                >
                  <MenuItem key="active" value="active">
                    Active
                  </MenuItem>
                  <MenuItem key="inactive" value="inactive">
                    Inactive
                  </MenuItem>
                </Select>
              </>
            )}
            <Box display="flex" flexDirection="row" gap="10px" width="100%">
              <Box display="flex" flexDirection="column" gap="10px" flexGrow={1} maxWidth="50%">
                <Typography variant="h3">Access</Typography>
                <Select
                  value={readPermission}
                  onChange={(event) => {
                    setReadPermission(event.target.value as DeploymentPermission);
                  }}
                >
                  {Object.entries(DeploymentPermissionLabels).map(([key, label]) => (
                    <MenuItem key={key} value={DeploymentPermissionValues[label]}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {readPermission === DeploymentPermission.SPECIFIC_ROLES && (
                  <>
                    <Typography variant="h3">Access Roles</Typography>
                    <CustomInputValidation
                      field_type="Role list"
                      value={readRoles}
                      onChange={setReadRoles}
                      valueError={false}
                      errorMessage=""
                    />
                  </>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap="10px" flexGrow={1} maxWidth="50%">
                <Typography variant="h3">Edit</Typography>
                <Select
                  value={editPermission}
                  onChange={(event) =>
                    setEditPermission(event.target.value as DeploymentPermission)
                  }
                >
                  {Object.entries(DeploymentPermissionLabels).map(([key, label]) => (
                    <MenuItem key={key} value={DeploymentPermissionValues[label]}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {editPermission === DeploymentPermission.SPECIFIC_ROLES && (
                  <>
                    <Typography variant="h3">Edit Roles</Typography>
                    <CustomInputValidation
                      field_type="Role list"
                      value={editRoles}
                      onChange={setEditRoles}
                      valueError={false}
                      errorMessage=""
                    />
                  </>
                )}
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              bgcolor={theme.palette.custom.grey[50]}
              flexGrow={1}
              padding="5px"
              border={`1px solid ${theme.palette.custom.secondaryBorder}`}
              borderRadius="3px"
            >
              <Typography variant="small">
                Global throttling across all deployments for an organization: 5 / min. 100 / Day.
              </Typography>
              <Typography
                variant="small"
                color={theme.palette.custom.hyperlink}
                onClick={() => openIntercom()}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Request Increase
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </SecondaryBox>
  );
};

type GuardrailDetailsSectionProps = {
  guardrail: any;
  setGuardRail: (val: any) => void;
};

const GuardrailDetailsSection = ({ guardrail, setGuardRail }: GuardrailDetailsSectionProps) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const theme = useTheme();

  // PII Settings
  const [piiGeneral, setPiiGeneral] = useState<boolean>(true);
  const [piiFinance, setPiiFinance] = useState<boolean>(true);
  const [piiIt, setPiiIt] = useState<boolean>(true);
  const [piiUsaSpecific, setPiiUsaSpecific] = useState<boolean>(true);
  const [piiCanadaSpecific, setPiiCanadaSpecific] = useState<boolean>(true);
  const [piiActive, setPiiActive] = useState<boolean>(false);

  // Toxicity Settings
  const [toxicityOption, setToxicityOption] = useState<"low" | "medium" | "high">("high");
  const [toxicityActive, setToxicityActive] = useState<boolean>(false);

  // Profanity Settings
  const [profanityActive, setProfanityActive] = useState<boolean>(false);

  // Update guardrail object whenever settings change
  useEffect(() => {
    const newGuardrail = {
      pii_general: piiActive && piiGeneral,
      pii_finance: piiActive && piiFinance,
      pii_it: piiActive && piiIt,
      pii_usa_specific: piiActive && piiUsaSpecific,
      pii_canada_specific: piiActive && piiCanadaSpecific,
      toxicity_low: toxicityActive && toxicityOption === "low",
      toxicity_medium: toxicityActive && toxicityOption === "medium",
      toxicity_high: toxicityActive && toxicityOption === "high",
      profanity: profanityActive,
    };
    setGuardRail(newGuardrail);
  }, [
    piiGeneral,
    piiFinance,
    piiIt,
    piiUsaSpecific,
    piiCanadaSpecific,
    piiActive,
    toxicityOption,
    toxicityActive,
    profanityActive,
    setGuardRail,
  ]);

  // Auto-disable PII if all sub-options are disabled
  useEffect(() => {
    if (!piiGeneral && !piiFinance && !piiIt && !piiUsaSpecific && !piiCanadaSpecific) {
      setPiiActive(false);
    }
  }, [piiGeneral, piiFinance, piiIt, piiUsaSpecific, piiCanadaSpecific]);

  return (
    <SecondaryBox>
      <Box display="flex" flexDirection="row" alignItems={"center"} gap="10px">
        <Box
          sx={{ cursor: "pointer" }}
          onClick={() => setExpanded(!expanded)}
          alignItems="center"
          display="flex"
        >
          <NavbarIcon variant={expanded ? "chevron-up-sm" : "chevron-down-sm"} />
        </Box>
        <Typography sx={{ cursor: "pointer" }} onClick={() => setExpanded(!expanded)} variant="h3">
          Guardrail Settings
        </Typography>
      </Box>
      {expanded && (
        <>
          <Divider sx={{ paddingTop: "10px" }} />
          <Box display="flex" paddingTop="10px" gap="10px" flexDirection="column">
            <Typography variant="body2">
              We only apply guardrails to user inputs and final outputs, and all filtering actions
              are fully logged in the trace — nothing is hidden.
            </Typography>
            <GuardrailsPIISettings
              generalActive={piiGeneral}
              setGeneralActive={setPiiGeneral}
              financeActive={piiFinance}
              setFinanceActive={setPiiFinance}
              itActive={piiIt}
              setItActive={setPiiIt}
              USAActive={piiUsaSpecific}
              setUSAActive={setPiiUsaSpecific}
              canadaActive={piiCanadaSpecific}
              setCanadaActive={setPiiCanadaSpecific}
              active={piiActive}
              setActive={setPiiActive}
            />
            <GuardrailsToxicitySettings
              option={toxicityOption}
              setOption={setToxicityOption}
              active={toxicityActive}
              setActive={setToxicityActive}
            />
            <GuardrailsProfanitySettings active={profanityActive} setActive={setProfanityActive} />
          </Box>
        </>
      )}
    </SecondaryBox>
  );
};

type GuardrailsToxicitySettingsProps = {
  option: "low" | "medium" | "high";
  setOption: (val: "low" | "medium" | "high") => void;
  active: boolean;
  setActive: (val: boolean) => void;
  readOnly?: boolean;
};

export const GuardrailsToxicitySettings = ({
  option,
  setOption,
  active,
  setActive,
  readOnly = false,
}: GuardrailsToxicitySettingsProps) => {
  return (
    <GuardrailSetting
      active={active}
      setActive={setActive}
      title="Toxicity Filtering"
      description="Filters content for the following categories: Hate, Insults, Sexual, and Misconduct."
      readOnly={readOnly}
    >
      <GuardrailOption
        active={option === "low"}
        setActive={() => setOption("low")}
        title="Low"
        description="Blocks only the most extreme toxic content such as threats or hate speech."
        readOnly={readOnly}
      />
      <GuardrailOption
        active={option === "medium"}
        setActive={() => setOption("medium")}
        title="Medium"
        description="Filters a balanced level of harmful language including insults, harassment, and offensive comments."
        readOnly={readOnly}
      />
      <GuardrailOption
        active={option === "high"}
        setActive={() => setOption("high")}
        title="High"
        description="Applies the strictest filtering to remove even mild toxicity or potentially harmful tone."
        readOnly={readOnly}
      />
    </GuardrailSetting>
  );
};

type GuardrailsPIISettingsProps = {
  generalActive: boolean;
  setGeneralActive: (val: boolean) => void;
  financeActive: boolean;
  setFinanceActive: (val: boolean) => void;
  itActive: boolean;
  setItActive: (val: boolean) => void;
  USAActive: boolean;
  setUSAActive: (val: boolean) => void;
  canadaActive: boolean;
  setCanadaActive: (val: boolean) => void;
  active: boolean;
  setActive: (val: boolean) => void;
  readOnly?: boolean;
};

export const GuardrailsPIISettings = ({
  generalActive,
  setGeneralActive,
  financeActive,
  setFinanceActive,
  itActive,
  setItActive,
  USAActive,
  setUSAActive,
  canadaActive,
  setCanadaActive,
  active,
  setActive,
  readOnly = false,
}: GuardrailsPIISettingsProps) => {
  return (
    <GuardrailSetting
      active={active}
      setActive={setActive}
      title="PII Filtering"
      description="Removes personally identifiable information from prompts and responses."
      readOnly={readOnly}
    >
      <GuardrailOption
        title="General"
        active={generalActive}
        setActive={setGeneralActive}
        description="When active, this will mask Phone, Email, Address, Age, Username, Password, Driver ID, License plate, Vehicle Identification number (VIN)."
        readOnly={readOnly}
      />
      <GuardrailOption
        title="Finance"
        active={financeActive}
        setActive={setFinanceActive}
        description="When active, this will mask CVV, Credit/Debit card expiry, Credit/Debit card number, Personal identification number (PIN), International Bank Account Number, SWIFT code"
        readOnly={readOnly}
      />
      <GuardrailOption
        title="IT"
        active={itActive}
        setActive={setItActive}
        description="When active, this will mask IPv4 address, Media access control (MAC) address, Web address, AWS access key, AWS secret key"
        readOnly={readOnly}
      />
      <GuardrailOption
        title="USA Specific"
        active={USAActive}
        setActive={setUSAActive}
        description="When active, this will mask US passport number, US Social Security Number (SSN), US Individual Taxpayer identification Number (ITIN), US bank account number, US bank account routing number"
        readOnly={readOnly}
      />
      <GuardrailOption
        active={canadaActive}
        setActive={setCanadaActive}
        title="Canada Specific"
        description="When active, this will mask Canadian Health Service Number, Canadian Social Insurance Number (SIN)"
        readOnly={readOnly}
      />
    </GuardrailSetting>
  );
};

type GuardrailsProfanitySettingsProps = {
  active: boolean;
  setActive: (val: boolean) => void;
  readOnly?: boolean;
};

export const GuardrailsProfanitySettings = ({
  active,
  setActive,
  readOnly = false,
}: GuardrailsProfanitySettingsProps) => {
  return (
    <GuardrailSetting
      active={active}
      setActive={setActive}
      title="Profanity Filtering"
      description="Blocks profane and offensive language from prompts and responses."
      readOnly={readOnly}
    >
      <></>
      {/* No sub-options for profanity - it's a simple on/off toggle */}
    </GuardrailSetting>
  );
};

type GuardrailSettingProps = {
  title: string;
  description: string;
  children: React.ReactNode;
  active: boolean;
  setActive: (val: boolean) => void;
  readOnly?: boolean;
};

export const GuardrailSetting = ({
  title,
  description,
  children,
  active,
  setActive,
  readOnly = false,
}: GuardrailSettingProps) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState<boolean>(false);

  useEffect(() => {
    if (!active) {
      setExpanded(false);
    } else if (readOnly && active) {
      // In read-only mode, expand if active to show the settings
      setExpanded(true);
    }
  }, [active, readOnly]);

  return (
    <Box display="flex" flexDirection="column">
      <Box
        display="flex"
        border={`1px solid ${theme.palette.custom.primaryBorder}`}
        borderRadius="4px"
        sx={{
          ...(expanded && {
            borderBottomRightRadius: "0px",
            borderBottomLeftRadius: "0px",
          }),
        }}
        padding="10px 10px"
        flexDirection="row"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        flexGrow={1}
        bgcolor={theme.palette.custom.blue[50]}
      >
        <Box display="flex" flexDirection="column" gap="5px">
          <Typography variant="h4" color={theme.palette.custom.hyperlink}>
            {title}
          </Typography>
          <Typography variant="small">{description}</Typography>
        </Box>
        <Switch
          checked={active}
          disabled={readOnly}
          onChange={(_, checked) => {
            if (!readOnly) {
              setActive(checked);
              if (checked) {
                setExpanded(true);
              } else {
                setExpanded(false);
              }
            }
          }}
          edge="end"
        />
      </Box>
      {expanded && children}
    </Box>
  );
};

type GuardrailOptionProps = {
  title: string;
  description: string;
  active: boolean;
  setActive: (val: boolean) => void;
  readOnly?: boolean;
};

export const GuardrailOption = ({
  title,
  description,
  active,
  setActive,
  readOnly = false,
}: GuardrailOptionProps) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      border={`1px solid ${theme.palette.custom.primaryBorder}`}
      sx={{
        borderTop: 0,
      }}
      padding="5px 10px"
      flexDirection="row"
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      flexGrow={1}
    >
      <SwitchWithTitle
        bold={true}
        orientation="space_between"
        onSelect={() => setActive(!active)}
        selected={active}
        title={title}
        description={description}
        disabled={readOnly}
      />
    </Box>
  );
};
